<template>
  <section class="bm-title">
    <header class="bm-title__head">
      <div class="bm-title__crumb">
        <button @click="$emit('back')">‹ 返回</button>
        <span>
          云库工作台　›　批量修改　›
          <strong>删除商品</strong>
        </span>
        <a>帮助教程</a>
      </div>
      <h1>批量删除商品</h1>
      <nav>
        <button class="mode-range" :class="{ active: mode === 'range' }" @click="switchMode('range')">
          按范围修改
        </button>
        <button class="mode-goods" :class="{ active: mode === 'goods' }" @click="switchMode('goods')">
          按商品修改
        </button>
      </nav>
    </header>
    <main class="bm-title__body">
      <section v-if="mode === 'range'" class="bm-title__filter">
        <div v-for="field in fields" :key="field.key" class="bm-title__field">
          <label>{{ field.label }}</label>
          <el-select v-if="field.select" v-model="filterValues[field.key]" class="bm-title__filter-select">
            <el-option label="全部" value="" />
            <el-option v-for="o in optionsFor(field.key)" :key="o.value" :label="o.label" :value="o.value" />
          </el-select>
          <div v-else-if="field.range" class="bm-title__date-range">
            <input v-model="addTime[0]" type="date" class="bm-title__date-input" />
            <span>—</span>
            <input v-model="addTime[1]" type="date" class="bm-title__date-input" />
          </div>
          <el-select
            v-else-if="field.key === 'group_id' || field.key === 'child_group_id'"
            v-model="filterValues[field.key]"
            class="bm-title__filter-select"
            @change="field.key === 'group_id' && onGroupChange()">
            <el-option label="全部" value="" />
            <el-option v-for="o in optionsFor(field.key)" :key="o.value" :label="o.label" :value="o.value" />
          </el-select>
          <input v-else v-model="filterValues[field.key]" :placeholder="field.placeholder" />
        </div>
        <footer>
          <div>
            <button @click="previewBatch">查询</button>
            <button class="plain" @click="reset">重置</button>
          </div>
        </footer>
      </section>
      <section v-else class="bm-title__picked">
        <button @click="openSelector">选择商品</button>
        <span>{{ selected.length ? '已选择 ' + selected.length + ' 个商品' : '请先选择需要修改的商品' }}</span>
      </section>
      <section class="bm-title__methods price-methods">
        <header>
          <p></p>
          删除条件（三选一）
        </header>
        <article>
          <div
            v-for="item in deleteRules"
            :key="item.value"
            class="price-method"
            :class="{ active: form.deleteRule === item.value }">
            <div class="price-method-title">
              <el-radio v-model="form.deleteRule" :label="item.value" @change="onRuleChange">
                {{ item.label }}
              </el-radio>
            </div>
            <div class="price-method-fields">
              <template v-if="item.value === 'percent_exceed'">
                <el-input-number
                  class="price-number-input"
                  v-model="form.percentValue"
                  :min="0"
                  :max="10000"
                  :step="0.01"
                  :precision="2"
                  controls-position="right"
                  placeholder="请输入百分比" />
                <span>%</span>
              </template>
              <template v-else-if="item.value === 'amount_exceed'">
                <el-input-number
                  class="price-number-input"
                  v-model="form.amountValue"
                  :min="0"
                  :max="999999"
                  :step="0.01"
                  :precision="2"
                  controls-position="right"
                  placeholder="请输入金额" />
                <span>元</span>
              </template>
            </div>
          </div>
        </article>
      </section>
      <section v-if="preview" class="bm-title__preview">
        <header>
          预览：共
          <b>{{ preview.product_count || preview.total || previewGoods.length || 0 }}</b>
          个商品，命中删除
          <b>{{ preview.matched_count || 0 }}</b>
          个，未命中
          <b>{{ preview.unmatched_count || 0 }}</b>
          个
        </header>
        <table>
          <thead>
            <tr>
              <th>商品信息</th>
              <th>SKU价格统计</th>
              <th>状态</th>
              <th>命中原因</th>
              <th>操作</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="(item, index) in previewGoods"
              :key="String(item.product_id || index)"
              :class="{
                'delete-matched-row': item.matched === true,
                'delete-unmatched-row': item.matched === false,
                'is-excluded': excludedProductIds.includes(String(item.product_id))
              }">
              <td>
                <el-image
                  class="bm-title__image"
                  :src="imageOf(item)"
                  fit="cover"
                  :preview-src-list="[imageOf(item)]" />
                <div>
                  <p>{{ item.title || item.product_title }}</p>
                  <div class="preview-meta">
                    <small>商品ID：{{ item.product_id }}</small>
                    <small>来源ID：{{ item.source_product_id || '无数据' }}</small>
                    <small>数据源：{{ sourceLabel(item.source_web) }}</small>
                  </div>
                </div>
              </td>
              <td class="price-cell">
                <div class="price-stats-grid">
                  <div>最低价：{{ item.min_price != null ? '¥' + Number(item.min_price).toFixed(2) : '无数据' }}</div>
                  <div>最高价：{{ item.max_price != null ? '¥' + Number(item.max_price).toFixed(2) : '无数据' }}</div>
                  <div>均价：{{ item.avg_price != null ? '¥' + Number(item.avg_price).toFixed(2) : '无数据' }}</div>
                </div>
              </td>
              <td>
                <span v-if="item.matched" class="status-matched">命中删除</span>
                <span v-else class="status-unmatched">未命中删除</span>
              </td>
              <td>
                <span class="match-reason">{{ item.match_reason || item.reason || '—' }}</span>
              </td>
              <td class="action-cell">
                <div class="action-buttons">
                  <button @click="toggleExclude(item)">
                    {{ excludedProductIds.includes(String(item.product_id)) ? '恢复删除' : '排除删除' }}
                  </button>
                  <button @click="toggleSku(item)">
                    {{ item._skuOpen ? '收起明细' : 'SKU明细' }}
                  </button>
                </div>
                <div v-if="item._skuOpen" class="bm-title__sku-detail">
                  <div v-if="item._skuLoading" class="sku-loading">加载中...</div>
                  <table v-else-if="skuRows(item).length">
                    <thead>
                      <tr>
                        <th>规格</th>
                        <th>价格</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="sku in skuRows(item)" :key="String(sku.sku_id)">
                        <td>{{ sku.sku_spec_text || sku.spec_text || '默认规格' }}</td>
                        <td>{{ sku.price != null ? '¥' + Number(sku.price).toFixed(2) : '无数据' }}</td>
                      </tr>
                    </tbody>
                  </table>
                  <p v-else-if="!item._skuError">暂无SKU明细</p>
                  <p v-if="item._skuError" class="sku-error">{{ item._skuError }}</p>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
        <footer class="bm-title__pagination">
          <button :disabled="previewPage.page <= 1" @click="previewBatchPage(previewPage.page - 1)">上一页</button>
          <span>第 {{ previewPage.page }} / {{ pageCount }} 页</span>
          <button :disabled="previewPage.page >= pageCount" @click="previewBatchPage(previewPage.page + 1)">
            下一页
          </button>
          <select v-model.number="previewPage.limit" @change="previewBatchPage(1)">
            <option :value="10">10条/页</option>
            <option :value="20">20条/页</option>
            <option :value="50">50条/页</option>
          </select>
        </footer>
      </section>
      <p v-if="preview && preview.warnings && preview.warnings.length" class="bm-title__warnings">
        {{ preview.warnings.map((item) => item.message + '（商品ID：' + item.product_id + '）').join('；') }}
      </p>
    </main>
    <footer class="bm-title__bottom">
      <button class="preview" :disabled="previewLoading" @click="previewBatch">
        {{ previewLoading ? '预览中' : '删除内容预览' }}
      </button>
      <button class="start" :disabled="!canCreate || submitting" @click="createBatch" title="点击预览检查无误后，再点击开始批量修改">
        {{ submitting ? '提交中' : '开始批量修改' }}
      </button>
    </footer>
    <div v-if="selecting" class="bm-title__mask">
      <div class="bm-title__select-modal">
        <header>
          <strong>选择修改商品</strong>
          <button @click="selecting = false">×</button>
        </header>
        <nav>
          <button :class="{ active: selectorTab === 'all' }" @click="switchSelectorTab('all')">选择商品</button>
          <button :class="{ active: selectorTab === 'selected' }" @click="switchSelectorTab('selected')">已选择</button>
        </nav>
        <div class="bm-title__selector-toolbar">
          <div class="bm-title__select-filter">
            <el-select
              v-model="selectorFilters.group_id"
              popper-class="bm-title__select-popper"
              class="bm-title__group-select"
              @change="loadSelectorGroups">
              <el-option label="全部分组" value="" />
              <el-option v-for="g in groupList" :key="g.id" :label="g.name || g.group_name" :value="g.id" />
            </el-select>
            <el-select
              v-model="selectorFilters.child_group_id"
              popper-class="bm-title__select-popper"
              class="bm-title__group-select"
              @change="loadGoods(1)">
              <el-option label="全部子分组" value="" />
              <el-option v-for="g in selectorChildGroups" :key="g.id" :label="g.name || g.group_name" :value="g.id" />
            </el-select>
            <input v-model="selectorFilters.source_product_id" placeholder="来源ID" />
            <input v-model="selectorFilters.keyword" placeholder="商品标题" />
            <button @click="loadGoods(1)">查询</button>
          </div>
          <div class="bm-title__selector-pagination">
            <button :disabled="selectorPage.page <= 1" @click="loadGoods(selectorPage.page - 1)">上一页</button>
            <span>{{ selectorPage.page }} / {{ Math.max(1, Math.ceil(selectorPage.total / selectorPage.limit)) }}</span>
            <button
              :disabled="selectorPage.page >= Math.max(1, Math.ceil(selectorPage.total / selectorPage.limit))"
              @click="loadGoods(selectorPage.page + 1)">
              下一页
            </button>
            <el-select
              v-model="selectorPage.limit"
              popper-class="bm-title__select-popper"
              class="bm-title__page-size"
              @change="loadGoods(1)">
              <el-option v-for="n in [10, 20, 50]" :key="n" :label="n + '条/页'" :value="n" />
            </el-select>
          </div>
          <label class="bm-title__select-all">
            <el-checkbox :value="selectorPageAllSelected" @change="togglePageGoods" />
            全选
          </label>
        </div>
        <main>
          <button
            v-for="g in displayedGoods"
            :key="g.product_id"
            :class="{ selected: selected.includes(String(g.product_id)) }"
            @click="toggleGood(g.product_id)">
            <el-checkbox
              :value="selected.includes(String(g.product_id))"
              @click.native.stop
              @change="toggleGood(g.product_id)" />
            <img :src="imageOf(g)" alt="商品主图" />
            <span>{{ g.title || g.product_title }}</span>
            <small>{{ priceRange(g) }}</small>
          </button>
        </main>
        <footer>
          <span>已选 {{ selected.length }} 个商品</span>
          <button class="plain" @click="selecting = false">取消</button>
          <button @click="selecting = false">确定</button>
        </footer>
      </div>
    </div>
  </section>
</template>
<script>
import {
  previewCloudProductDeleteBatch,
  previewCloudProductDeleteBatchSkus,
  createCloudProductDeleteBatch
} from '@/api/main/cloudBatchEdit'
import { cloudProductGroups, cloudProductList } from '@/api/main/ucenter'

const fields = [
  { key: 'group_id', label: '分组', placeholder: '请选择分组' },
  { key: 'child_group_id', label: '子分组', placeholder: '请选择子分组' },
  { key: 'created_range', label: '导入时间', range: true },
  { key: 'source_product_id', label: '来源ID', placeholder: '请输入来源ID' },
  { key: 'sku_status', label: 'SKU替换状态', select: true },
  { key: 'sku_mark_status', label: '商品标记状态', select: true },
  { key: 'ai_title_status', label: '标题替换状态', select: true },
  { key: 'source_web', label: '数据源', select: true }
]

export default {
  name: 'BatchModifyDelete',
  data() {
    return {
      mode: 'range',
      fields,
      addTime: [],
      groupList: [],
      childGroupList: [],
      statusOptions: {
        sku_status: [
          { label: '待替换', value: 1 },
          { label: '已替换', value: 2 },
          { label: '找不到', value: 3 }
        ],
        sku_mark_status: [
          { label: '未标记', value: 1 },
          { label: '已标记', value: 2 },
          { label: '找不到', value: 3 }
        ],
        ai_title_status: [
          { label: '待替换', value: 1 },
          { label: '已替换', value: 2 },
          { label: '异常', value: 9 }
        ],
        source_web: [
          { value: 9, label: '星好货' },
          { value: 3, label: 'TB' },
          { value: 4, label: 'PDD' },
          { value: 2, label: '1688' },
          { value: 5, label: 'DY' },
          { value: 6, label: 'WX' }
        ]
      },
      filterValues: {
        group_id: '',
        child_group_id: '',
        source_product_id: '',
        sku_status: '',
        sku_mark_status: '',
        ai_title_status: '',
        source_web: ''
      },
      selecting: false,
      selectorTab: 'all',
      selectorPage: { page: 1, limit: 20, total: 0 },
      selectorFilters: { group_id: '', child_group_id: '', source_product_id: '', keyword: '' },
      selectorChildGroups: [],
      selected: [],
      selectedGoods: [],
      goods: [],
      preview: null,
      previewGoods: [],
      previewPage: { page: 1, limit: 20, total: 0 },
      excludedProductIds: [],
      previewKey: '',
      previewLoading: false,
      submitting: false,
      form: {
        productIds: '',
        keyword: '',
        deleteRule: 'avg_half',
        percentValue: null,
        amountValue: null
      },
      deleteRules: [
        { value: 'avg_half', label: '商品最低价SKU小于等于该商品全部SKU均价的一半' },
        { value: 'percent_exceed', label: '商品最高价SKU超过最低价的百分比' },
        { value: 'amount_exceed', label: '商品最高价SKU超过最低价的金额' }
      ]
    }
  },
  watch: {
    form: {
      deep: true,
      handler() {
        this.invalidate()
      }
    },
    filterValues: {
      deep: true,
      handler() {
        this.invalidate()
      }
    },
    addTime: {
      deep: true,
      handler() {
        this.invalidate()
      }
    },
    selected: {
      deep: true,
      handler() {
        this.invalidate()
      }
    },
    excludedProductIds: {
      deep: true,
      handler() {
        this.invalidate()
      }
    }
  },
  computed: {
    imageOf() {
      return (g) => g.main_image || g.image_url || (Array.isArray(g.main_images) && g.main_images[0]) || ''
    },
    displayedGoods() {
      if (this.selectorTab === 'selected') {
        const s = (this.selectorPage.page - 1) * this.selectorPage.limit
        return this.selectedGoods.slice(s, s + this.selectorPage.limit)
      }
      return this.goods
    },
    selectorPageAllSelected() {
      return (
        this.displayedGoods.length > 0 && this.displayedGoods.every((g) => this.selected.includes(String(g.product_id)))
      )
    },
    canCreate() {
      return !!this.preview && this.previewKey === this.payloadKey()
    },
    pageCount() {
      return Math.max(1, Math.ceil(this.previewPage.total / this.previewPage.limit))
    }
  },
  created() {
    this.loadGroups()
  },
  methods: {
    onRuleChange() {
      // 切换规则时重置参数
      if (this.form.deleteRule === 'avg_half') {
        this.form.percentValue = null
        this.form.amountValue = null
      }
    },
    skuRows(item) {
      return item.skus || item.sku_list || item._skus || []
    },
    toggleSku(item) {
      const id = String(item.product_id)
      if (item._skuOpen) {
        this.$set(item, '_skuOpen', false)
        return
      }
      this.$set(item, '_skuLoading', true)
      this.$set(item, '_skuOpen', true)
      const p = this.buildPayload()
      previewCloudProductDeleteBatchSkus({
        ...p,
        product_id: id
      })
        .then((r) => {
          const d = (r.data && (r.data.data || r.data)) || {}
          this.$set(item, '_skus', Array.isArray(d) ? d : d.skus || d.items || d.list || [])
        })
        .catch(() => this.$set(item, '_skuError', 'SKU查询失败'))
        .finally(() => this.$set(item, '_skuLoading', false))
    },
    sourceLabel(value) {
      const labels = { 9: '星好货', 3: 'TB', 4: 'PDD', 2: '1688', 5: 'DY', 6: 'WX' }
      return labels[String(value)] || value || '未知'
    },
    priceRange(good) {
      const min = good.min_price ?? good.price_min ?? good.price
      const max = good.max_price ?? good.price_max ?? min
      return min === undefined || min === null ? '价格待定' : `¥${min}~¥${max}`
    },
    switchSelectorTab(t) {
      this.selectorTab = t
      if (t === 'selected') {
        this.selectorPage.total = this.selectedGoods.length
        this.selectorPage.page = 1
      } else this.loadGoods(1)
    },
    loadSelectorGroups() {
      const g = this.groupList.find((x) => String(x.id) === String(this.selectorFilters.group_id))
      this.selectorChildGroups = (g && g.child_groups) || []
      this.selectorFilters.child_group_id = ''
      this.loadGoods(1)
    },
    toggleGood(id) {
      id = String(id)
      if (!id || id === 'undefined' || id === 'null') return
      const i = this.selected.indexOf(id)
      if (i >= 0) {
        this.selected.splice(i, 1)
        this.selectedGoods = this.selectedGoods.filter((g) => String(g.product_id) !== id)
      } else {
        this.selected.push(id)
        const g = this.goods.find((x) => String(x.product_id) === id)
        if (g) this.selectedGoods.push(g)
      }
    },
    togglePageGoods(c) {
      this.displayedGoods.forEach((g) => {
        const id = String(g.product_id)
        if (!id || id === 'undefined' || id === 'null') return
        if (c && !this.selected.includes(id)) {
          this.selected.push(id)
          this.selectedGoods.push(g)
        }
        if (!c) {
          const i = this.selected.indexOf(id)
          if (i >= 0) this.selected.splice(i, 1)
          this.selectedGoods = this.selectedGoods.filter((x) => String(x.product_id) !== id)
        }
      })
    },
    loadGoods(page = 1) {
      return cloudProductList({ page, page_size: this.selectorPage.limit, status: 1, ...this.selectorFilters })
        .then((r) => {
          const d = (r.data && (r.data.data || r.data)) || {}
          this.goods = d.list || d.data || d
          this.selectorPage = { ...this.selectorPage, page, total: Number(d.total || this.goods.length) }
        })
        .catch(() => this.$message.error('商品查询失败'))
    },
    switchMode(m) {
      this.mode = m
      this.previewPage.page = 1
      this.invalidate()
    },
    invalidate() {
      this.preview = null
      this.previewKey = ''
    },
    async loadGroups() {
      try {
        const r = await cloudProductGroups({ with_count: 0 })
        this.groupList = (r.data && (r.data.data || r.data)) || []
      } catch (e) {
        this.$message.error('请求失败，请稍后重试')
      }
    },
    onGroupChange() {
      const g = this.groupList.find((i) => String(i.id) === String(this.filterValues.group_id))
      this.childGroupList = (g && g.child_groups) || []
      this.filterValues.child_group_id = ''
      this.invalidate()
    },
    optionsFor(k) {
      return k === 'group_id'
        ? this.groupList.map((i) => ({ value: i.id, label: i.name || i.group_name }))
        : k === 'child_group_id'
        ? this.childGroupList.map((i) => ({ value: i.id, label: i.name || i.group_name }))
        : this.statusOptions[k] || []
    },
    buildPayload() {
      const f = this.form
      const ids =
        this.mode === 'goods'
          ? this.selected.map(String)
          : f.productIds
              .split(/[,，\\r\\n]+/)
              .map((v) => v.trim())
              .filter(Boolean)
      const p = {
        product_filter: {
          ...this.filterValues,
          product_ids: ids,
          excluded_product_ids: this.excludedProductIds.map(String),
          created_start: this.addTime[0] || '',
          created_end: this.addTime[1] || '',
          keyword: f.keyword
        },
        delete_rule: { type: f.deleteRule }
      }

      // 添加规则参数
      if (f.deleteRule === 'percent_exceed') {
        p.delete_rule.percent_value = f.percentValue
      } else if (f.deleteRule === 'amount_exceed') {
        p.delete_rule.amount_value = f.amountValue
      }

      return p
    },
    payloadKey() {
      return JSON.stringify(this.buildPayload())
    },
    validate() {
      const p = this.buildPayload()
      if (this.mode === 'goods' && !p.product_filter.product_ids.length) return '请选择商品'

      // 验证删除规则参数
      if (p.delete_rule.type === 'percent_exceed') {
        if (
          p.delete_rule.percent_value === null ||
          p.delete_rule.percent_value === undefined ||
          p.delete_rule.percent_value === ''
        ) {
          return '请输入百分比参数'
        }
        if (Number(p.delete_rule.percent_value) < 0) {
          return '百分比不能为负数'
        }
      } else if (p.delete_rule.type === 'amount_exceed') {
        if (
          p.delete_rule.amount_value === null ||
          p.delete_rule.amount_value === undefined ||
          p.delete_rule.amount_value === ''
        ) {
          return '请输入金额参数'
        }
        if (Number(p.delete_rule.amount_value) < 0) {
          return '金额不能为负数'
        }
      }

      return ''
    },
    async previewBatch() {
      const e = this.validate()
      if (e) return this.$message.warning(e)
      this.previewLoading = true
      try {
        const r = await previewCloudProductDeleteBatch({
          ...this.buildPayload(),
          page: this.previewPage.page,
          limit: this.previewPage.limit
        })
        const body = r && r.data && r.data.code !== undefined ? r.data : r
        if (body && body.code && body.code !== 200) throw Error(body.msg || body.message || '预览失败')
        this.preview = (body && (body.data || body)) || {}
        this.previewGoods = this.preview.list || this.preview.products || this.preview.items || []
        this.previewPage.total = Number(
          this.preview.product_count || this.preview.total || this.previewGoods.length || 0
        )
        this.previewKey = this.payloadKey()
      } catch (e) {
        this.invalidate()
        this.$message.error(
          (e.response && e.response.data && (e.response.data.msg || e.response.data.message)) ||
            e.message ||
            '请求失败，请稍后重试'
        )
      } finally {
        this.previewLoading = false
      }
    },
    async previewBatchPage(page) {
      this.previewPage.page = page
      await this.previewBatch()
    },
    async createBatch() {
      if (!this.canCreate) return this.$message.warning('请先预览当前删除规则')

      // 检查商品数量
      const totalProducts = this.previewPage?.total || 0
      if (totalProducts <= 0) {
        this.$message.warning('商品数小于0，请选择商品后点击修改预览')
        return
      }

      // 弹出确认提示
      try {
        await this.$confirm(`商品数${totalProducts}，是否开始修改？`, '确认修改', {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          type: 'warning'
        })
      } catch (e) {
        // 用户点击取消
        return
      }

      this.submitting = true
      try {
        const r = await createCloudProductDeleteBatch(this.buildPayload())
        const body = r && r.data && r.data.code !== undefined ? r.data : r
        if (body && body.code && body.code !== 200) throw Error(body.msg || body.message || '删除任务创建失败')
        this.$message.success('删除任务已创建')
        this.$emit('back')
      } catch (e) {
        this.$message.error(
          (e.response && e.response.data && (e.response.data.msg || e.response.data.message)) ||
            e.message ||
            '请求失败，请稍后重试'
        )
      } finally {
        this.submitting = false
      }
    },
    toggleExclude(g) {
      const id = String(g.product_id),
        i = this.excludedProductIds.indexOf(id)
      if (!id || id === 'undefined' || id === 'null') return
      i < 0 ? this.excludedProductIds.push(id) : this.excludedProductIds.splice(i, 1)
      this.invalidate()
    },
    reset() {
      Object.assign(this.filterValues, {
        group_id: '',
        child_group_id: '',
        source_product_id: '',
        sku_status: '',
        sku_mark_status: '',
        ai_title_status: '',
        source_web: ''
      })
      this.addTime = []
      Object.assign(this.form, {
        productIds: '',
        keyword: '',
        deleteRule: 'avg_half',
        percentValue: null,
        amountValue: null
      })
      this.selected = []
      this.invalidate()
    },
    async openSelector() {
      this.selecting = true
      this.selectorTab = 'all'
      await this.loadGroups()
      this.loadSelectorGroups()
      await this.loadGoods(1)
    }
  }
}
</script>
<style scoped>
/* 复用库存页面样式，添加删除特定样式 */
.bm-title {
  width: 100%;
  max-width: 100%;
  min-width: 0;
  box-sizing: border-box;
  min-height: calc(100vh - 56px);
  overflow-x: hidden;
  background: #f5f6f8;
  color: #1a1a2e;
  font-family: 'Noto Sans SC', sans-serif;
}

.bm-title__preview tr:nth-child(even) {
  background: #fcfcfd;
}

.bm-title__preview tr:hover {
  background: #f0f7ff;
}

.bm-title__preview tr.is-excluded {
  background: #e5e5e5;
  color: #777;
}

.status-matched {
  padding: 2px 8px;
  border: 1px solid #ffccd2;
  border-radius: 3px;
  background: #fff5f5;
  color: #c8443c;
  font-size: 11px;
  font-weight: 600;
}

.status-unmatched {
  padding: 2px 8px;
  border: 1px solid #b7eb8f;
  border-radius: 3px;
  background: #f6ffed;
  color: #52c41a;
  font-size: 11px;
  font-weight: 600;
}

.match-reason {
  color: #666;
  font-size: 11px;
}

.bm-title button,
.bm-title input,
.bm-title select,
.bm-title textarea {
  font: inherit;
}

.bm-title button {
  cursor: pointer;
}

.bm-title__head {
  background: #fff;
  border-bottom: 1px solid #e0e0ec;
  padding: 10px 20px 0;
}

.bm-title__crumb {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  color: #999;
}

.bm-title__crumb button {
  height: 26px;
  padding: 0 10px;
  border: 1px solid #e0e0ec;
  border-radius: 4px;
  background: #fff;
  color: #666;
}

.bm-title__crumb a {
  margin-left: auto;
  color: #1677ff;
}

.bm-title h1 {
  margin: 12px 0 8px;
  font: 600 18px 'Noto Serif SC', serif;
}

.bm-title__head nav {
  display: flex;
  gap: 22px;
}

.bm-title__head nav button {
  padding: 9px 6px;
  border: 0;
  border-bottom: 2px solid transparent;
  background: #fff;
  color: #666;
  font-size: 13px;
}

.bm-title__head nav button.active {
  border-bottom-color: #c8443c;
  color: #c8443c;
  font-weight: 600;
}

.bm-title__body {
  padding: 14px 20px 82px;
}

.bm-title__filter {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 10px 14px;
  padding: 14px 16px 10px;
  border: 2px solid #e0e0ec;
  border-radius: 8px;
  background: #fff;
}

.bm-title__field label {
  display: block;
  margin-bottom: 4px;
  color: #666;
  font-size: 10px;
}

.bm-title__field input,
.bm-title__field select,
.bm-title__range input {
  box-sizing: border-box;
  width: 100%;
  height: 28px;
  padding: 4px 8px;
  border: 1px solid #e0e0ec;
  border-radius: 4px;
  background: #fff;
  font-size: 11px;
}

.bm-title__range {
  display: flex;
  align-items: center;
  gap: 5px;
}

.bm-title__range span {
  color: #999;
}

.bm-title__filter footer {
  grid-column: 1/-1;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 8px;
  padding-top: 9px;
  border-top: 1px solid #f0f0f0;
  font-size: 12px;
}

.bm-title__filter button,
.bm-title__picked button {
  height: 28px;
  padding: 0 13px;
  border: 0;
  border-radius: 4px;
  background: #c8443c;
  color: #fff;
  font-size: 12px;
}

.bm-title__filter .plain {
  border: 1px solid #e0e0ec;
  background: #fff;
  color: #666;
}

.bm-title__picked {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
  margin-top: 14px;
  padding: 10px 14px;
  border: 1px solid #e0e0ec;
  border-radius: 6px;
  background: #fff;
  color: #666;
  font-size: 12px;
}

.bm-title__picked button {
  background: #1677ff;
}

.bm-title__picked span {
  color: #1677ff;
}

.bm-title__methods {
  overflow: hidden;
  margin-top: 14px;
  border: 2px solid #e0e0ec;
  border-radius: 8px;
  background: #fff;
}

.bm-title__methods > header {
  padding: 10px 14px;
  border-bottom: 1px solid #e0e0ec;
  background: #f5f0e8;
  font-size: 12px;
  font-weight: 600;
}

.bm-title__methods > header p {
  display: inline-block;
  width: 3px;
  height: 14px;
  margin-right: 7px;
  border-radius: 2px;
  background: #c8443c;
  vertical-align: middle;
}

.bm-title__preview {
  overflow: hidden;
  margin-top: 14px;
  border: 2px solid #e0e0ec;
  border-radius: 8px;
  background: #fff;
}

.bm-title__preview header {
  padding: 10px 14px;
  border-bottom: 1px solid #e0e0ec;
  font-size: 12px;
}

.bm-title__preview header b {
  color: #1677ff;
}

.bm-title__preview table {
  width: 100%;
  border-collapse: collapse;
}

.bm-title__preview th {
  padding: 8px 12px;
  background: #f5f0e8;
  color: #666;
  font-size: 11px;
  font-weight: 500;
  text-align: left;
}

.bm-title__preview tr {
  border-bottom: 1px solid #f0f0f0;
}
.bm-title__preview td {
  padding: 10px 12px;
  font-size: 11px;
}

.bm-title__preview td:first-child {
  display: flex;
  align-items: center;
  gap: 8px;
}

.bm-title__preview p {
  margin: 3px 0 0;
}

.bm-title__preview small {
  line-height: 16px;
  color: #999;
}

.bm-title__preview .preview-meta {
  display: flex;
  align-items: center;
  flex-wrap: nowrap;
  gap: 10px;
  margin-top: 4px;
  white-space: nowrap;
  line-height: 16px;
}

.bm-title__preview .preview-meta small {
  display: inline;
  margin: 0;
}

.bm-title__image {
  display: inline-flex;
  position: relative;
  z-index: 1;
  width: 46px;
  height: 46px;
  align-items: center;
  justify-content: center;
  border: 1px solid #ffccd2;
  border-radius: 4px;
  background: #fff5f5;
  object-fit: cover;
  font-size: 16px;
  cursor: pointer;
}

::v-deep(.bm-title__image .el-image__inner) {
  border-radius: 4px;
  cursor: pointer;
}

.bm-title__pagination {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
  padding: 10px 14px;
  border-top: 1px solid #f0f0f0;
  color: #666;
  font-size: 12px;
}

.bm-title__pagination button {
  padding: 4px 10px;
  border: 1px solid #d9e2f0;
  border-radius: 4px;
  background: #fff;
  color: #1677ff;
}

.bm-title__pagination button:disabled {
  color: #bbb;
  cursor: not-allowed;
}

.bm-title__pagination select {
  height: 26px;
  border: 1px solid #d9e2f0;
  border-radius: 4px;
}

.bm-title__bottom {
  position: fixed;
  left: var(--merchant-sidebar-width, 210px);
  right: 0;
  z-index: 20;
  bottom: 0;
  display: flex;
  width: auto;
  max-width: none;
  min-width: 0;
  box-sizing: border-box;
  overflow-x: hidden;
  flex-wrap: nowrap;
  align-items: center;
  gap: 10px;
  padding: 10px 20px;
  border-top: 1px solid #e0e0ec;
  background: #fff;
  box-shadow: 0 -2px 8px rgba(0, 0, 0, 0.06);
  font-size: 12px;
}

.bm-title__bottom button {
  min-width: 0;
  flex: 0 1 auto;
  height: 30px;
  padding: 0 14px;
  border: 0;
  border-radius: 4px;
  color: #fff;
}

.bm-title__bottom .preview {
  background: #1677ff;
}

.bm-title__bottom .start {
  margin-left: auto;
  margin-right: 50px;
  padding: 0 28px;
  background: #52c41a;
}

.bm-title__mask {
  position: fixed;
  z-index: 3000;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  background: rgba(0, 0, 0, 0.4);
  backdrop-filter: blur(3px);
}

.bm-title__select-modal {
  width: min(1180px, 96vw);
  min-width: 900px;
  overflow: hidden;
  border: 1px solid #e0e0ec;
  border-radius: 8px;
  background: #fff;
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.18);
}

.bm-title__select-modal > header {
  display: flex;
  justify-content: space-between;
  padding: 12px 18px;
  border-bottom: 1px solid #e0e0ec;
  background: #fafafa;
  font-size: 14px;
}

.bm-title__select-modal > header button {
  width: 24px;
  height: 24px;
  border: 1px solid #e0e0ec;
  border-radius: 4px;
  background: #fff;
  color: #666;
  font-size: 18px;
}

.bm-title__select-modal nav {
  display: flex;
  padding: 0 18px;
  border-bottom: 1px solid #e0e0ec;
}

.bm-title__select-modal nav button {
  padding: 10px 14px;
  border: 0;
  border-bottom: 2px solid transparent;
  background: #fff;
  color: #666;
  font-size: 12px;
}

.bm-title__select-modal nav button.active {
  border-bottom-color: #1677ff;
  color: #1677ff;
}

.bm-title__selector-toolbar {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 18px;
  background: #fafafa;
}

.bm-title__selector-toolbar .bm-title__select-filter {
  flex: 1;
  padding: 0;
  background: transparent;
}

.bm-title__select-filter {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  padding: 10px 18px;
  background: #fafafa;
}

.bm-title__select-filter input {
  width: 150px;
  height: 28px;
  padding: 4px 8px;
  border: 1px solid #e0e0ec;
  border-radius: 4px;
  font-size: 11px;
}

.bm-title__select-filter button {
  height: 28px;
  padding: 0 12px;
  border: 0;
  border-radius: 4px;
  background: #1677ff;
  color: #fff;
  font-size: 11px;
}

.bm-title__select-filter .bm-title__group-select {
  width: 108px;
}

.bm-title__select-all {
  display: flex;
  flex-shrink: 0;
  align-items: center;
  margin-left: auto;
  white-space: nowrap;
  gap: 6px;
  font-size: 12px;
}

.bm-title__selector-pagination {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
  padding: 0;
  color: #666;
  font-size: 12px;
}

.bm-title__selector-pagination button {
  padding: 3px 8px;
  border: 1px solid #dcdfe6;
  border-radius: 4px;
  background: #fff;
  color: #606266;
}

.bm-title__selector-pagination button:disabled {
  cursor: not-allowed;
  opacity: 0.5;
}

.bm-title__page-size {
  width: 96px;
}

.bm-title__select-modal main {
  display: grid;
  grid-template-columns: repeat(6, minmax(0, 1fr));
  gap: 10px;
  max-height: 350px;
  padding: 14px 18px;
  overflow: auto;
}

.bm-title__select-modal main button {
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 8px;
  border: 2px solid #e0e0ec;
  border-radius: 6px;
  background: #fff;
  color: #1a1a2e;
  text-align: left;
  min-height: 150px;
}

.bm-title__select-modal main button.selected {
  border-color: #1677ff;
  background: #e6f4ff;
}

.bm-title__select-modal main img {
  width: 100%;
  height: 159px;
  object-fit: contain;
  background: #fff5f5;
}

.bm-title__select-modal main span {
  overflow: hidden;
  font-size: 10px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.bm-title__select-modal main small {
  color: #999;
  font-size: 10px;
}

.bm-title__select-modal footer {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  align-items: center;
  padding: 10px 18px;
  border-top: 1px solid #e0e0ec;
  font-size: 12px;
}

.bm-title__select-modal footer > span {
  margin-right: auto;
}

.bm-title__select-modal footer button {
  height: 28px;
  padding: 0 13px;
  border: 0;
  border-radius: 4px;
  background: #1677ff;
  color: #fff;
  font-size: 12px;
}

.bm-title__select-modal footer .plain {
  border: 1px solid #e0e0ec;
  background: #fff;
  color: #666;
}

.bm-title__field {
  min-width: 0;
}

.bm-title__preview .action-cell {
  min-width: 110px;
  white-space: nowrap;
}

.action-buttons {
  display: flex;
  gap: 8px;
  margin-bottom: 8px;
}

.bm-title__preview .action-cell button {
  min-width: 82px;
  height: 30px;
  padding: 0 12px;
  border: 1px solid #91caff;
  border-radius: 5px;
  background: #e6f4ff;
  color: #1677ff;
  font-size: 12px;
  line-height: 28px;
  transition: 0.2s;
}

.bm-title__sku-detail {
  margin-top: 8px;
  padding: 8px;
  border: 1px solid #e0e0ec;
  border-radius: 4px;
  background: #fafafa;
}

.bm-title__sku-detail table {
  width: 100%;
  border-collapse: collapse;
}

.bm-title__sku-detail th {
  padding: 4px 8px;
  background: #f5f0e8;
  color: #666;
  font-size: 11px;
  font-weight: 500;
  text-align: left;
  border-bottom: 1px solid #e0e0ec;
}

.bm-title__sku-detail td {
  padding: 4px 8px;
  border-bottom: 1px solid #f0f0f0;
  font-size: 11px;
}

.sku-loading {
  padding: 8px;
  text-align: center;
  color: #999;
  font-size: 11px;
}

.sku-error {
  padding: 8px;
  color: #f56c6c;
  font-size: 11px;
}

.bm-title__preview .action-cell button:hover,
.bm-title__preview .action-cell button:focus {
  border-color: #409eff;
  background: #bae0ff;
  color: #0958d9;
}

.price-cell {
  min-width: 150px;
  line-height: 24px;
  white-space: nowrap;
}

.price-stats-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 4px 8px;
  margin-bottom: 8px;
}

.price-stats-grid > div {
  font-size: 11px;
}

.bm-title__head nav .mode-range {
  color: #1677ff;
}

.bm-title__head nav .mode-goods {
  color: #722ed1;
}

.bm-title__head nav .mode-range.active {
  border-bottom-color: #1677ff;
  background: #f0f7ff;
}

.bm-title__head nav .mode-goods.active {
  border-bottom-color: #722ed1;
  background: #f9f0ff;
  color: #722ed1;
}

::v-deep(.el-input__inner),
::v-deep(.el-input__icon) {
  height: 28px;
  line-height: 28px;
}

.bm-title__filter-select {
  display: block;
  width: 100%;
}

.bm-title__date-range {
  display: flex;
  align-items: center;
  width: 100%;
  min-width: 0;
  gap: 5px;
}

.bm-title__date-input {
  flex: 1 1 0;
  min-width: 0;
  box-sizing: border-box;
  width: 0;
  height: 28px;
  padding: 4px 8px;
  border: 1px solid #e0e0ec;
  border-radius: 4px;
  background: #fff;
  color: #666;
  font-size: 11px;
}

::v-deep(.bm-title__filter-select .el-input__inner) {
  height: 28px;
  padding: 0 28px 0 8px;
  border-color: #e0e0ec;
  border-radius: 4px;
  color: #1a1a2e;
  font-size: 12px;
  line-height: 28px;
  text-align: left;
}

::v-deep(.bm-title__filter-select .el-input__suffix) {
  right: 7px;
}

::v-deep(.bm-title__filter-select .el-input__icon) {
  color: #999;
  font-size: 14px;
  line-height: 28px;
}

::v-deep(.bm-title__select-filter .bm-title__group-select .el-input),
::v-deep(.bm-title__select-filter .bm-title__group-select .el-input__inner) {
  width: 108px;
  height: 28px;
  line-height: 28px;
}

::v-deep(.bm-title__selector-pagination .bm-title__page-size .el-input),
::v-deep(.bm-title__selector-pagination .bm-title__page-size .el-input__inner) {
  height: 28px;
  line-height: 28px;
}

.price-methods article {
  display: block;
  padding: 0;
}

.price-method {
  position: relative;
  display: flex;
  align-items: flex-start;
  padding: 12px 16px;
  border: 0;
  border-bottom: 1px solid #e1e4eb;
  border-radius: 0;
  background: #fff;
  transition: background 0.2s;
}

.price-method:hover {
  background: #fafbfd;
}

.price-method.active {
  box-shadow: none;
}

.price-method-title {
  flex: 0 0 auto;
  min-height: 32px;
  display: flex;
  align-items: center;
  padding: 0 12px 0 0;
}

.price-method-title .el-radio {
  display: block;
  width: 100%;
  margin: 0;
  line-height: 24px;
}

.price-method-title .el-radio__label {
  color: #172b4d;
  font-weight: 600;
}

.price-method-fields {
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px 10px;
  margin: 0;
  padding: 0 0 2px;
  border: 0;
  background: transparent;
}

.price-method-fields > span {
  color: #172b4d;
  font-size: 14px;
  font-weight: 600;
}

.price-number-input {
  width: 160px;
  height: 28px;
  display: inline-flex;
  vertical-align: middle;
  line-height: 28px;
}

.price-number-input .el-input-number__increase,
.price-number-input .el-input-number__decrease {
  width: 22px;
  right: 1px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  line-height: 1;
  color: #606266;
  background: #f5f7fa;
  border-color: #dcdfe6;
}

.price-number-input .el-input-number__increase {
  top: 1px;
  height: 15px;
  border-radius: 0 3px 0 0;
}

.price-number-input .el-input-number__decrease {
  bottom: 1px;
  height: 15px;
  border-radius: 0 0 3px 0;
}

.price-number-input .el-input-number__increase i,
.price-number-input .el-input-number__decrease i {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  font-size: 12px;
  line-height: 1;
}

.price-number-input .el-input-number__increase:hover,
.price-number-input .el-input-number__decrease:hover {
  color: var(--el-color-primary, #409eff);
  background: #ecf5ff;
}

.price-number-input .el-input__inner {
  padding-left: 15px;
  padding-right: 26px;
  text-align: left;
}

@media (max-width: 960px) {
  .bm-title__filter {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }

  .bm-title__select-modal {
    min-width: 0;
  }

  .bm-title__select-modal main {
    grid-template-columns: repeat(4, minmax(0, 1fr));
  }

  .bm-title__selector-toolbar {
    flex-wrap: wrap;
  }
}

@media (max-width: 720px) {
  .bm-title__filter {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .bm-title__select-modal main {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .bm-title__bottom .start {
    margin-left: 0;
  }
}

@media (max-width: 520px) {
  .bm-title__head,
  .bm-title__body {
    padding-right: 12px;
    padding-left: 12px;
  }

  .bm-title__filter {
    grid-template-columns: 1fr;
  }

  .bm-title__select-modal main {
    grid-template-columns: repeat(2, 1fr);
  }

  .bm-title__bottom {
    left: 0;
    gap: 6px;
    padding: 8px 12px;
  }

  .bm-title__bottom .start {
    padding: 0 12px;
  }

  .bm-title__bottom .preview {
    display: none;
  }

  .price-method {
    display: block;
  }

  .price-method-title {
    width: 100%;
    padding: 0;
  }

  .price-method-fields {
    margin-top: 8px;
  }
}
</style>
<style>
.el-popper.bm-title__select-popper {
  z-index: 4000 !important;
}
</style>
